import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Check, X, AlertTriangle, Download, Loader2 } from 'lucide-react'
import { useSitemaps, useSitemapCoverage, useExportCrawl, type SitemapRecord } from '../hooks/useCrawls'

function CoverageCard({ label, value, total, variant }: {
  label: string
  value: number
  total?: number
  variant?: 'default' | 'destructive' | 'warning' | 'success'
}) {
  const color =
    variant === 'destructive' ? 'text-destructive' :
    variant === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
    variant === 'success' ? 'text-green-600 dark:text-green-400' : ''
  return (
    <div className="text-center">
      <p className={`text-2xl font-bold tabular-nums ${color}`}>{value.toLocaleString()}</p>
      {total !== undefined && total > 0 && (
        <p className="text-xs text-muted-foreground tabular-nums">{Math.round((value / total) * 100)}%</p>
      )}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

function SitemapList({ sitemaps }: { sitemaps: SitemapRecord[] }) {
  if (sitemaps.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-yellow-500 mb-2" />
          <p className="text-sm text-muted-foreground">No sitemaps discovered during crawl.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Check robots.txt for Sitemap: directives, or ensure /sitemap.xml exists.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Discovered Sitemaps</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">URL</TableHead>
                <TableHead className="text-xs w-[80px]">Type</TableHead>
                <TableHead className="text-xs w-[80px] text-right">Entries</TableHead>
                <TableHead className="text-xs w-[70px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sitemaps.map((sm) => (
                <TableRow key={sm.id}>
                  <TableCell className="text-xs font-mono max-w-[400px] truncate" title={sm.url}>
                    <a href={sm.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {sm.url}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      {sm.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-right tabular-nums">
                    {sm.entry_count.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {sm.valid ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <span className="flex items-center gap-1 text-destructive">
                        <X className="h-4 w-4" />
                        <span className="text-[10px] truncate max-w-[100px]" title={sm.error_message || ''}>
                          {sm.error_message || 'Invalid'}
                        </span>
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

type UrlTab = 'in_sitemap_only' | 'in_crawl_only'

function CoverageUrls({ urls }: { urls: { inSitemapOnly: string[]; inCrawlOnly: string[] } }) {
  const [tab, setTab] = useState<UrlTab>('in_sitemap_only')

  const current = tab === 'in_sitemap_only' ? urls.inSitemapOnly : urls.inCrawlOnly

  if (urls.inSitemapOnly.length === 0 && urls.inCrawlOnly.length === 0) {
    return null
  }

  const pills: { label: string; value: UrlTab; count: number }[] = [
    { label: 'In Sitemap Only', value: 'in_sitemap_only', count: urls.inSitemapOnly.length },
    { label: 'In Crawl Only', value: 'in_crawl_only', count: urls.inCrawlOnly.length },
  ]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">URL Cross-Reference</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {pills.map((p) => (
            <button
              key={p.value}
              onClick={() => setTab(p.value)}
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${
                tab === p.value
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-muted/50 border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {p.label} ({p.count})
            </button>
          ))}
        </div>

        {current.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {tab === 'in_sitemap_only' ? 'All sitemap URLs were found in the crawl.' : 'All crawled pages are listed in the sitemap.'}
          </p>
        ) : (
          <div className="rounded-md border max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">URL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {current.map((url, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono max-w-[600px] truncate" title={url}>
                      {url}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {current.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            Showing {current.length} URLs{current.length >= 200 && ' (limited to 200)'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function SitemapTab({ crawlId }: { crawlId: string }) {
  const { data: sitemapsData, isLoading: sitemapsLoading } = useSitemaps(crawlId)
  const hasSitemaps = (sitemapsData?.sitemaps?.length ?? 0) > 0
  const { data: coverage, isLoading: coverageLoading } = useSitemapCoverage(crawlId, hasSitemaps)
  const exportCrawl = useExportCrawl()

  if (sitemapsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  const sitemaps = sitemapsData?.sitemaps ?? []

  return (
    <div className="space-y-4">
      {/* Generate sitemap button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={exportCrawl.isPending}
          onClick={() => exportCrawl.mutate({ id: crawlId, type: 'sitemap-xml', format: 'json' })}
        >
          {exportCrawl.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Generate Sitemap XML
        </Button>
      </div>

      {/* Coverage overview (only if sitemaps exist) */}
      {coverage && (
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <CoverageCard label="Sitemap URLs" value={coverage.totalSitemapUrls} />
              <CoverageCard label="Crawled Pages" value={coverage.totalCrawledPages} />
              <CoverageCard
                label="Matched"
                value={coverage.inBoth}
                total={coverage.totalSitemapUrls}
                variant="success"
              />
              <CoverageCard
                label="Coverage"
                value={coverage.coveragePercent}
                variant={coverage.coveragePercent >= 90 ? 'success' : coverage.coveragePercent >= 50 ? 'warning' : 'destructive'}
              />
              <CoverageCard
                label="In Sitemap Only"
                value={coverage.inSitemapOnly}
                variant={coverage.inSitemapOnly > 0 ? 'warning' : 'default'}
              />
              <CoverageCard
                label="Not in Sitemap"
                value={coverage.inCrawlOnly}
                variant={coverage.inCrawlOnly > 0 ? 'warning' : 'default'}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {coverageLoading && hasSitemaps && <Skeleton className="h-24" />}

      <SitemapList sitemaps={sitemaps} />

      {coverage && <CoverageUrls urls={coverage.urls} />}
    </div>
  )
}
